import React from 'react';
import { Field, RadioGroup } from '@chakra-ui/react';
import { Controller, type RegisterOptions, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type FormRadioButtonsProps = {
	name: string;
	label: string | React.ReactNode;
	isRequired?: boolean;
	registerOptions?: Partial<RegisterOptions>;
	options: Array<{ value: string; label: string; dataCy?: string; props?: RadioGroup.ItemProps }>;
	radioGroupProps?: Partial<RadioGroup.RootProps>;
	helperText?: string;
};

export const FormRadioButtons: React.FC<FormRadioButtonsProps> = ({ name, label, isRequired, registerOptions, radioGroupProps, helperText, options }) => {
	const { t } = useTranslation();
	const { control } = useFormContext();

	return (
		<Controller
			name={name}
			control={control}
			rules={{
				...(isRequired && {
					required: t('general.required'),
				}),
				...registerOptions,
			}}
			render={({ field, fieldState }) => (
				<Field.Root as="fieldset" invalid={fieldState.invalid} required={isRequired} colorPalette={'brand.primary'}>
					{label && (
						<Field.Label>
							{label} <Field.RequiredIndicator />
						</Field.Label>
					)}
					<RadioGroup.Root
						display="flex"
						rowGap={2}
						columnGap={4}
						onChange={field.onChange}
						value={field.value as string | null | undefined}
						flexWrap="wrap"
						ref={field.ref}
						{...radioGroupProps}
					>
						{options.map(({ value, label: optionLabel, props, dataCy }, i) => (
							<RadioGroup.Item key={value + i} id={value + i} value={value} {...props}>
								<RadioGroup.ItemHiddenInput onBlur={field.onBlur} />
								<RadioGroup.ItemIndicator data-cy={dataCy} />
								<RadioGroup.ItemText>{optionLabel}</RadioGroup.ItemText>
							</RadioGroup.Item>
						))}
					</RadioGroup.Root>
					{helperText && <Field.HelperText>{helperText}</Field.HelperText>}
					<Field.ErrorText>{fieldState.error?.message}</Field.ErrorText>
				</Field.Root>
			)}
		/>
	);
};
